import { useMemo, useState } from "react";
import type { LexBot } from "../types";

interface Props {
  bots: LexBot[];
  selectedId: string | null;
  onSelect: (bot: LexBot) => void;
}

/**
 * The bot picker: one card per bot.
 *
 * A grid rather than a list because the whole estate should be visible at a
 * glance - on a projector, a scrolling list means the audience watches
 * someone hunt rather than watching the tool.
 */
/**
 * The grid before the bots arrive.
 *
 * Same shape and spacing as the real cards, so the list resolves in place
 * instead of the page jumping when it lands - on a projector that jump is the
 * most visible thing in the room. Three of them: the list is filtered to
 * tagged bots and is usually short, so promising a full screen would be a
 * bigger lie than saying nothing.
 */
export function BotGridSkeleton() {
  return (
    <div className="grid" aria-hidden="true">
      {[0, 1, 2].map((i) => (
        <div key={i} className="pod pod-skeleton">
          <span className="pod-top">
            <span className="sk sk-icon" />
          </span>
          <span className="sk sk-line" style={{ width: "62%" }} />
          <span className="sk sk-line sk-thin" />
          <span className="sk sk-line sk-thin" style={{ width: "40%" }} />
        </div>
      ))}
    </div>
  );
}

export function BotGrid({ bots, selectedId, onSelect }: Props) {
  const [query, setQuery] = useState("");

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return bots;
    return bots.filter(
      (bot) =>
        bot.name.toLowerCase().includes(q) ||
        bot.botId.toLowerCase().includes(q) ||
        (bot.description ?? "").toLowerCase().includes(q),
    );
  }, [bots, query]);

  if (!bots.length) {
    return <p className="muted">No Lex V2 bots in this region.</p>;
  }

  return (
    <>
      {bots.length > 6 && (
        <input
          className="search"
          type="search"
          placeholder={`Filter ${bots.length} bots\u2026`}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          aria-label="Filter bots"
        />
      )}

      <div className="grid" role="radiogroup" aria-label="Lex bots">
        {shown.map((bot) => {
          const selected = selectedId === bot.botId;
          return (
            <button
              key={bot.botId}
              type="button"
              role="radio"
              aria-checked={selected}
              className={`pod ${selected ? "selected" : ""}`}
              onClick={() => onSelect(bot)}
            >
              <span className="pod-top">
                <span className="pod-icon" aria-hidden="true">
                  {bot.name.slice(0, 2).toUpperCase()}
                </span>
                {selected && (
                  <span className="pod-check" aria-hidden="true">
                    {"\u2713"}
                  </span>
                )}
              </span>

              <span className="pod-name">{bot.name}</span>
              {bot.description && (
                <span className="pod-desc">{bot.description}</span>
              )}

              <span className="pod-meta">
                <code>{bot.botId}</code>
                {bot.locales?.slice(0, 2).map((locale) => (
                  <span key={locale} className="tag">
                    {locale}
                  </span>
                ))}
              </span>
            </button>
          );
        })}
        {!shown.length && <p className="muted">No bot matches that.</p>}
      </div>
    </>
  );
}
